import type { ENOPOrganType } from '#shared';

// The NOP resources boundary (BE ADR CODE-012, `/api/nop/*`).
//
// These endpoints sit BESIDE `/api/hubsResourceReport` rather than replacing it.
// The report serves one pre-computed, hub-grouped, organ-bucketed shape; these
// serve the same upstream data with NOP's own field names, enum values and
// resource-type boundaries, so a consumer can compute its own view.
//
// Everything here mirrors NOP. Nothing is renamed on the way through, which is
// the point — a field that reads oddly reads oddly upstream too.

/**
 * Where a NOP resources read stands, as three facts rather than two.
 *
 * `absent` and `empty` must not collapse into one nullish check. `absent` means
 * the entry was never written: outside the swept window, or before the first
 * sweep, so the caller should widen its request or retry. `empty` means it WAS
 * written and genuinely holds nothing: no consoles at that hub. Rendering
 * "nothing scheduled" for a date that was simply never fetched is precisely the
 * lie CODE-012 section 6 exists to prevent.
 */
export type NopResourcesStatus = 'absent' | 'empty' | 'present';

/** Provenance envelope the BE stamps on every cached entry. */
export interface NopResourcesMeta {
  /** When the BE wrote the entry, NOT when NOP generated it. */
  generatedAt: string;
  /** The NOP window the sweep fetched, for date-partitioned entries. */
  from?: string | null;
  to?: string | null;
}

/** One NOP resources read: the payload plus how much of it is real. */
export interface NopResourcesRead<T> {
  status: NopResourcesStatus;
  /** Derived from `status`; kept because the wire contract carries it. */
  cached: boolean;
  data: T | null;
  meta: NopResourcesMeta | null;
}

/**
 * A date-partitioned read: one {@link NopResourcesRead} per date, composing nothing.
 *
 * The BE deliberately does not merge a range into one payload — each date keeps
 * its own status, so a partially warm range stays honest about which days are
 * which. A caller that wants one list across the range does that merge itself.
 */
export interface NopResourcesDateRangeRead<T> {
  requested: { from: string; to: string; hub?: string };
  days: Record<string, NopResourcesRead<T>>;
}

/**
 * NOP's `Common.Queries.LocationView`.
 *
 * `timezone` is the field that matters: hub-local time on the dashboard comes
 * from the hub's LOCATION, not from the hub record.
 */
export interface NopLocation {
  id: string;
  country: string | null;
  regionId: string | null;
  region: string | null;
  state: string | null;
  city: string | null;
  /** IANA zone name. */
  timezone: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** A hub as NOP describes it, with its own `name` and short `code`. */
export interface NopHub {
  id: string;
  name: string | null;
  /** Short display code, e.g. the three-letter hub code. */
  code: string | null;
  location: NopLocation;
  isSuperHub: boolean;
}

/**
 * NOP's own console status.
 *
 * Three values, and deliberately not the report's `EResourceStatus`
 * (`Available | Assigned | OnRest`), which is a BE-invented mapping over this —
 * `OnRest` is not a console state at all.
 */
export enum ENopConsoleStatus {
  Available = 'Available',
  OnCase = 'OnCase',
  Reserved = 'Reserved',
}

/**
 * One `OnCase` or `OnRest` node from `/api/nop/employeeReservations`, as NOP's
 * `EmployeesReservationsData+Node`.
 *
 * Returned whole when it overlaps the requested window, so `from` can precede the
 * window and `to` can follow it. `to` is null while the node is still open, which
 * must read as "still running", never as "ended at epoch".
 */
export interface NopEmployeeReservationWireNode {
  nodeId: string;
  from: string;
  to: string | null;
  /**
   * `Reserved | OnCase | OnRest` upstream; the endpoint serves `OnCase` and
   * `OnRest`, and drops `Reserved`.
   */
  type: string;
  caseId: string | null;
  /** e.g. `ANHE457 LI`: the UNOS donor id plus an organ code. */
  caseName: string | null;
}

/**
 * The endpoint's bare body. An employee with no nodes is absent from the map,
 * which means "no past assignments", not a failure.
 */
export interface NopEmployeeReservationsBody {
  employees: Record<string, NopEmployeeReservationWireNode[]> | null;
}

/** A node with the employee id it was keyed by, so the map can be an array. */
export interface NopEmployeeReservationNode extends NopEmployeeReservationWireNode {
  employeeId: string;
}

/**
 * A reservation tying a resource to a case.
 *
 * The only link from a console or an employee to the case they are on. The
 * status filter matters: NOP keeps historic reservations on the record, so
 * taking the first one attributes a console to a case it finished days ago.
 */
export interface NopReservation {
  caseId: string;
  /**
   * Human-readable case label, e.g. `ANHE457 LI` — the UNOS donor id plus an
   * organ code. Present on the reservation itself, so a chip can be labelled
   * without joining the case feed.
   */
  caseName?: string | null;
  status: string;
  /** When the booking was made. The only instant the reservation itself carries. */
  createdAt?: string | null;
}

/**
 * An active rest period, as an INTERVAL.
 *
 * Recorded because an earlier pass called this a point-in-time flag and dropped
 * the design's rest overlay for having no source. It has a start, a duration, a
 * planned end and an actual end, which is everything an overlay needs. 21 of 806
 * employees carry one live.
 */
export interface NopRestPeriod {
  restStartTime: string;
  restEndTime: string;
  /** Where the rest actually ended; differs from `restEndTime` once revised. */
  actualRestEndTime: string | null;
  /** Non-null once the rest was cancelled early, which ends it. */
  restCancellationTime: string | null;
  /** The case the rest follows, when it follows one. */
  caseId: string | null;
  caseName: string | null;
}

/** A trial programme the employee is enrolled on, FOR ONE ORGAN. */
export interface NopTrialProgram {
  id: string;
  name: string | null;
  /** The organ this enrolment covers — what makes the badge's `T` per-organ. */
  organ: ENOPOrganType;
  isActive: boolean;
}

/**
 * NOP's `Employees.EmployeeData`, narrowed to what this screen reads.
 *
 * **The BE's `EmployeeDto` is deliberately partial** — it says so, and warns that
 * absence of a drift signal proves only the DECLARED fields match upstream. An
 * earlier pass read that DTO, took four missing fields as four fields that do not
 * exist, and documented the design's rest overlay, case chips and per-organ badge
 * flags as unsourceable. All four are here.
 */
export interface NopEmployee {
  id: string;
  firstName: string | null;
  lastName: string | null;
  /** Organs certified on — the badge row. */
  organSpecializations: ENOPOrganType[] | null;
  /**
   * Per-organ Proctor / Shadow training, which IS the design's `Pr`/`Sh` tags.
   *
   * Both are empty on every one of the 806 live employees, so the tags render
   * nothing today. That is a populated-upstream question, not a mapping gap: the
   * fields exist and are read here, so they light up when the data arrives.
   */
  proctorOrganSpecializations: ENOPOrganType[] | null;
  shadowOrganSpecializations: ENOPOrganType[] | null;
  trialPrograms: NopTrialProgram[] | null;
  /** At most one live commitment per employee; there is no assignment history. */
  reservations: NopReservation[] | null;
  activeRestPeriod: NopRestPeriod | null;
}

/**
 * NOP's `ClinicalSpecialist.CSShift`.
 *
 * Two fields are easy to conflate and matter:
 * - `day` is the shift's OWNING day, distinct from `from`/`to`, which can cross
 *   midnight. Anything bucketing by day must use `day`.
 * - `isTravel` and `isExtra` are MODIFIERS, not types. `TravelDay` is separately
 *   a `shiftType`, so the two coexist.
 */
export interface NopCSShift {
  scheduleId: string;
  /** Owning day. Not `from`. */
  day: string;
  from: string;
  to: string;
  resourceId: string;
  /** `Actual | Deprecated | InPlanningProcess`. Never re-derive from dates. */
  shiftStatus: string;
  shiftType: string;
  organs: ENOPOrganType[] | null;
  regionId: string | null;
  /** Nullable upstream: a shift need not be tied to a hub. */
  hubId: string | null;
  isTravel?: boolean;
  isExtra?: boolean;
}

/**
 * The specialist behind a shift, as NOP nests it in the schedule response.
 *
 * This is why the specialist panel needs no second request: name, photo and the
 * organ certifications all ride along with the shifts. `organs` is the list of
 * organs the specialist is certified on, which is exactly the badge row.
 *
 * What is NOT here, and has no source anywhere: the design's per-organ `Pr`/`Sh`
 * training roles. `employee.roles` is job titles (`clinical-specialist`), not
 * training, so those tags cannot be populated at all.
 */
export interface NopCSResource {
  id: string;
  firstName: string | null;
  lastName: string | null;
  profilePhoto: string | null;
  /** Organs the specialist is certified on — the certification badges. */
  organs: ENOPOrganType[] | null;
  /** Organs they can lead phone support for; a narrower certification. */
  phoneSupportOrgans: ENOPOrganType[] | null;
  canPhoneSupport: boolean;
  regionId: string | null;
  hubId: string | null;
}

/**
 * One resource's segment of a shift partition.
 *
 * `days` is keyed by day, and a resource holds at most one shift per day because
 * NOP nests it as a map rather than a list.
 */
export interface NopCSScheduleSegment {
  resource: NopCSResource;
  days: Record<string, NopCSShift> | null;
}

/**
 * One date-and-hub partition of clinical specialist shifts.
 *
 * NOP's own response shape, narrowed to one day: same `resourcesShifts` nesting,
 * same `resource` block. `from`/`to` describe the FETCH that produced it, not the
 * day — the day is in the key and in each record's own `day`.
 */
export interface NopShiftPartition {
  from: string;
  to: string;
  resourcesShifts: Record<string, NopCSScheduleSegment>;
}

/**
 * NOP's `OcsConsoles.OcsConsoleData`.
 *
 * Carries seven fields the hubs resource report drops. The one that changes
 * behaviour is `isInMaintenance`: the report queries NOP with
 * `isInMaintenance=false`, so those consoles never reached a consumer at all,
 * and the device grid's maintenance state could not be exercised by live data.
 *
 * There is no elapsed-perfusion field. CODE-012 records that as the one genuine
 * upstream gap on this screen, not something the DTO is hiding.
 */
export interface NopConsole {
  id: string;
  ocsConsoleName: string | null;
  serialNumber: string | null;
  fullSerialNumber: string | null;
  /** The hub this console belongs to. Nullable: a console need not be at one. */
  nopHub: string | null;
  organType: ENOPOrganType;
  status: ENopConsoleStatus;
  isInMaintenance: boolean;
  caseId: string | null;
  caseName: string | null;
  caseNumber: string | null;
  reservations: NopReservation[] | null;
  trialProgram: { name?: string | null } | null;
}
